import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import '../cta-button';
import { styles } from './css';

import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';
class StyledMultiSelect extends LitElement {
  static get properties() {
    return {
      _expanded: { type: Boolean },
      _dataReady: { type: Boolean },
      _optionsData: { type: Array },
      _dataHeaderString: { type: String },
      _includeSearch: { type: Boolean },
      _searchString: { type: String },
    };
  }

  static get styles() {
    return [LIT_CSS_VARIABLES, SHARED_ELEMENTS, SHARED_CLASSES, styles];
  }

  constructor() {
    super();

    this._expanded = false;
    this._dataReady = false;
    this._optionsData = [];
    this._dataHeaderString = '';
    this._includeSearch = false;
    this._searchString = '';
    document.addEventListener('clearFilterSelections', e => {
      this.clearFilters();
      this._expanded = false;
    });
    document.addEventListener('visualizedMaterialSelected', e => {
      this._expanded = false;
      this.requestUpdate();
    });
  }

  async connectedCallback() {
    super.connectedCallback();
    this.processOptionsData();

    this._includeSearch = this.includeSearch;
    this._dataHeaderString = this.dataHeaderString;
  }

  disconnectedCallback() {
    document.removeEventListener('clearFilterSelections', e => {
      this.clearFilters();
    });
    super.disconnectedCallback();
  }

  render() {
    return this._dataReady
      ? html`
          <div class="multi-select-wrapper">
            <div class="header" @click="${this.toggleVisbility}">
              <p>${this._dataHeaderString}</p>
              <img
                class="${classMap({
                  'arrow-expanded': this._expanded,
                  'arrow-closed': !this._expanded,
                  'select-arrow': true,
                })}"
                src="/static/icons/SVG/arrow-black-5x10.svg"
              />
            </div>
            ${this._expanded
              ? html`
                  ${this._includeSearch ? this.searchHTML : html``}
                  <div class="options-content">
                    ${this._optionsData.length
                      ? this._optionsData.map(
                          side => html`
                            <div class="side">
                              <span class="option-category-header">
                                ${side.humanizedCategory}
                              </span>
                              ${side.options.map(
                                option =>
                                  html`
                                    <div class="filter-option">
                                      <label class="option-label">
                                        <span class="option-label-checkbox">
                                          <input
                                            type="checkbox"
                                            .checked=${option.selected}
                                            @click="${() => {
                                              option.selected = !option.selected;
                                              this.patternFilterSelected(
                                                side,
                                                option
                                              );
                                            }}"
                                          />
                                          <div></div>
                                        </span>
                                        <p class="option-label-text">
                                          ${option.humanizedOption}
                                        </p>
                                      </label>
                                    </div>
                                  `
                              )}
                            </div>
                          `
                        )
                      : html``}
                  </div>
                  <div class="options-buttons">
                    <cta-button
                      cta-type="secondary"
                      cta-data=${JSON.stringify({
                        url: '',
                        text: 'Clear All',
                        fullWidth: true,
                      })}
                      @click="${() => {
                        this.clearFilters(true);
                        this._expanded = false;
                      }}"
                    ></cta-button>
                    <cta-button
                      cta-type="primary"
                      cta-data=${JSON.stringify({
                        url: '',
                        text: 'Apply',
                        fullWidth: true,
                      })}
                      @click="${() => (this._expanded = false)}"
                    ></cta-button>
                  </div>
                `
              : html``}
          </div>
        `
      : html``;
  }

  get searchHTML() {
    return html`
      <div class="input-wrapper search">
        <input
          type="text"
          placeholder="Search by Name"
          class="search-input"
          .value=${this._searchString}
          @keyup=${e => {
            if (e.key === 'Enter' || e.keyCode === 13) {
              this._expanded = false;
            } else {
              this.updateSearchSelection(e.target.value);
            }
          }}
        />
        <img class="search-icon" src="/static/icons/SVG/search.svg" />
      </div>
    `;
  }

  updateSearchSelection(inputValue) {
    if (inputValue === this._searchString) return;
    if (inputValue.length !== 1) {
      this.clearFilters();
      this._searchString = inputValue;
      const searchFromFilterEvent = new CustomEvent('searchFromFilterUpdated', {
        detail: {
          inputValue,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(searchFromFilterEvent);
    }
  }

  get optionsDataFromParent() {
    return this.getAttribute('options-data')
      ? JSON.parse(this.getAttribute('options-data'))
      : '';
  }

  get dataHeaderString() {
    return window.useMobileLayout && this.includeSearch
      ? 'Search and Filter'
      : 'Filter by Color and Pattern';
  }

  get includeSearch() {
    this._includeSearch = !!this.getAttribute('data-include-search');
    return this._includeSearch;
  }

  processOptionsData() {
    if (!!this.optionsDataFromParent) {
      this.optionsDataFromParent.forEach(optionCategory => {
        if (optionCategory.options) {
          optionCategory.options.forEach(option => {
            option.selected = false;
          });
        }
        if (optionCategory.humanizedCategory === 'Color') {
          this._optionsData.splice(0, 0, optionCategory);
        } else if (optionCategory.humanizedCategory === 'Pattern') {
          this._optionsData.splice(1, 0, optionCategory);
        }
      });
      this._dataReady = true;
      this.requestUpdate();
    }
  }

  patternFilterSelected(category, option) {
    const selectedOptions = [];
    if (this._includeSearch) {
      this._searchString = '';
      this.updateSearchSelection('');
      this.requestUpdate();
    }
    this._optionsData.forEach(grouping => {
      const selectedOptionGroup = [];
      grouping.options.forEach(option => {
        if (option.selected) {
          selectedOptionGroup.push({
            id: option.value,
            category: grouping,
          });
        }
      });
      if (selectedOptionGroup.length > 0)
        selectedOptions.push(selectedOptionGroup);
    });

    let selectedFiltersString = '';
    if (selectedOptions.length > 0) {
      selectedFiltersString = '&tag_names=';
      selectedOptions.forEach(optionGroup => {
        if (selectedFiltersString !== '&tag_names=') {
          selectedFiltersString += ',';
        }
        const selectedOptionIds = optionGroup.map(option => option.id);
        const optionDelimiter = optionGroup[0].category?.inclusive ? '|' : ',';
        selectedFiltersString += selectedOptionIds.join(optionDelimiter);
      });
    }

    const patternFilterUpdatedEvent = new CustomEvent('patternFilterUpdated', {
      detail: {
        selectedFiltersString,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(patternFilterUpdatedEvent);

    if (!!category && !!option) {
      const clickAction = option.selected ? 'Select' : 'DeSelect';
      let analyticsEventString = `Visualization|Filter ${category.humanizedCategory}|${clickAction} ${option.humanizedOption}`;
      this.sendAdobeAnalyticsEvent(analyticsEventString);
    }
  }

  clearFilters(userInvoked = false) {
    this._optionsData.forEach(side => {
      side.options.forEach(option => (option.selected = false));
    });
    this.patternFilterSelected(null, null);
    if (userInvoked) {
      this.sendAdobeAnalyticsEvent('Visualization|Filter|DeSelect All');
    }
  }

  toggleVisbility() {
    this._expanded = !this._expanded;
  }

  sendAdobeAnalyticsEvent(eventStr) {
    const eventData = {
      type: 'tool',
      detail: {
        eVar35: eventStr,
      },
    };

    fireAdobeAnalyticsEvent(eventData);
  }
}
customElements.define('styled-multi-select', StyledMultiSelect);
